import { AdjustmentSettings, AdjustedLineItem } from '@/types/adjustment-filters';
import { evaluateFilterRules, getSourceLineItemData } from './adjustmentFilterUtils';

interface EstimateLineItem {
  id: string;
  component_type: string;
  component_id: string;
  quantity_in_base_units: number;
  unit_cost_snapshot: number;
  total_cost_snapshot: number;
}

/**
 * Calculates adjusted prices for line items based on adjustment settings
 */
export const calculateAdjustedPrices = async (
  lineItems: EstimateLineItem[],
  adjustmentSettings: AdjustmentSettings
): Promise<Map<string, AdjustedLineItem>> => {
  const adjustedItems = new Map<string, AdjustedLineItem>();

  for (const lineItem of lineItems) {
    const sourceData = await getSourceLineItemData(
      lineItem.component_type,
      lineItem.component_id
    );

    if (!sourceData) {
      // If we can't get source data, use the snapshot values
      adjustedItems.set(lineItem.id, {
        id: lineItem.id,
        originalUnitCost: lineItem.unit_cost_snapshot,
        originalTotalCost: lineItem.total_cost_snapshot,
        adjustedUnitCost: lineItem.unit_cost_snapshot,
        adjustedTotalCost: lineItem.total_cost_snapshot,
        markupRemoved: false,
        discountApplied: 0
      });
      continue;
    }

    // Check if this line item matches the filter rules
    const matchesFilters = evaluateFilterRules(adjustmentSettings.filters, {
      category_id: sourceData.category_id,
      trade_id: sourceData.trade_id,
      quantity_in_base_units: lineItem.quantity_in_base_units,
      base_cost: sourceData.base_cost
    });

    let adjustedUnitCost = lineItem.unit_cost_snapshot;
    let adjustedTotalCost = lineItem.total_cost_snapshot;
    let markupRemoved = false;

    // If filters match, remove markup (use base cost)
    if (matchesFilters) {
      adjustedUnitCost = sourceData.base_cost;
      adjustedTotalCost = sourceData.base_cost * lineItem.quantity_in_base_units;
      markupRemoved = true;
    }

    // Track discount amount (but don't apply to line item prices)
    let discountApplied = 0;
    if (adjustmentSettings.discountPercentage && adjustmentSettings.discountPercentage > 0) {
      if (adjustmentSettings.discountType === 'percentage') {
        discountApplied = adjustedTotalCost * (adjustmentSettings.discountPercentage / 100);
        // Don't modify adjustedTotalCost or adjustedUnitCost - discount will be shown separately
      }
    }

    adjustedItems.set(lineItem.id, {
      id: lineItem.id,
      originalUnitCost: lineItem.unit_cost_snapshot,
      originalTotalCost: lineItem.total_cost_snapshot,
      adjustedUnitCost,
      adjustedTotalCost,
      markupRemoved,
      discountApplied
    });
  }

  return adjustedItems;
};

/**
 * Calculates the total adjustment summary
 */
export const calculateAdjustmentSummary = (
  adjustedItems: Map<string, AdjustedLineItem>
) => {
  let originalTotal = 0;
  let adjustedTotal = 0;
  let totalMarkupRemoved = 0;
  let totalDiscountApplied = 0;
  let itemsWithMarkupRemoved = 0;

  adjustedItems.forEach((item) => {
    originalTotal += item.originalTotalCost;
    adjustedTotal += item.adjustedTotalCost;
    totalDiscountApplied += item.discountApplied;

    if (item.markupRemoved) {
      itemsWithMarkupRemoved++;
      totalMarkupRemoved += (item.originalTotalCost - (item.adjustedTotalCost + item.discountApplied));
    }
  });

  return {
    originalTotal,
    adjustedTotal,
    totalSavings: originalTotal - adjustedTotal,
    totalMarkupRemoved,
    totalDiscountApplied,
    itemsWithMarkupRemoved,
    savingsPercentage: originalTotal > 0 ? ((originalTotal - adjustedTotal) / originalTotal) * 100 : 0
  };
};
